import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

import gestopAPI, { gestopApiForExternalEndpoints } from 'utils/gestopAPI';
import { errorProcessor, getIriFromObj } from 'utils/processors';
import Loader from 'ui-component/Loader';
import useConfig from 'hooks/useConfig';
import authReducer, {
    initialAuthState,
    LOGIN,
    LOGOUT,
    UPDATE_USER,
    UPDATE_LICENSE,
    UPDATE_PROFILE_SETTINGS,
    DELETE_PROFILE_SETTINGS
} from 'contexts/auth/reducer';
import {
    ApiProfileSettingsPostCollection,
    ApiProfileSettingsDeleteCollection,
    ApiProfileSettingsPatchItem
} from 'store/app/admin/profile-setting';
import { buildConfigFromProfileSettings, clearAuthSession, loadAuthSession, saveAuthSession, verifyToken } from 'contexts/auth/session';

const hasUserData = (user = {}) => Object.keys(user || {}).length > 0;

const normalizeProfileSettingComparableValue = (value) => {
    if (Array.isArray(value)) {
        return JSON.stringify(value);
    }

    if (value && typeof value === 'object') {
        return JSON.stringify(value);
    }

    return value;
};

const hasSameProfileSettingValue = (currentValue, nextValue) =>
    normalizeProfileSettingComparableValue(currentValue) === normalizeProfileSettingComparableValue(nextValue);

const applyProfileSettingsToConfig = (updateConfig = () => {}, profileSettings = []) => {
    const userProfileConfig = buildConfigFromProfileSettings(profileSettings);

    if (Object.keys(userProfileConfig).length > 0) {
        updateConfig(userProfileConfig);
    }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const { updateConfig, resetTenantPresetColor } = useConfig();

    const [state, dispatch] = useReducer(authReducer, initialAuthState);

    const persistSession = (sessionOverrides = {}) =>
        saveAuthSession({
            serviceToken: state.serviceToken,
            refreshToken: state.refreshToken,
            keepMeLoggedIn: state.keepMeLoggedIn,
            user: state.user,
            profileSettings: state.profileSettings,
            license: state.license,
            ...sessionOverrides
        });

    const dumpUser = () => {
        clearAuthSession();
        resetTenantPresetColor();
        dispatch({ type: LOGOUT });
    };

    const hydrateSession = (session = {}) => {
        const normalizedSession = saveAuthSession(session);

        applyProfileSettingsToConfig(updateConfig, normalizedSession.profileSettings);
        dispatch({ type: LOGIN, payload: normalizedSession });

        return normalizedSession;
    };

    const requestTokenRefresh = async (refreshToken) => {
        const response = await gestopApiForExternalEndpoints.post('security/refreshtoken', { refresh_token: refreshToken });

        if (response.status !== 200) {
            return null;
        }

        const { token, refresh_token: nextRefreshToken, user, profileSettings, license } = response?.data || {};

        return {
            serviceToken: token || null,
            refreshToken: nextRefreshToken || refreshToken || null,
            user: user || {},
            profileSettings: profileSettings || [],
            license: license || {}
        };
    };

    const refreshToken = async (refreshTokenValue, sessionFallback = {}) => {
        let refreshedSession = null;

        try {
            refreshedSession = await requestTokenRefresh(refreshTokenValue);
        } catch (error) {
            refreshedSession = null;
        }

        if (!refreshedSession) {
            dumpUser();
            return null;
        }

        return hydrateSession({
            ...refreshedSession,
            keepMeLoggedIn:
                typeof sessionFallback.keepMeLoggedIn === 'boolean' ? sessionFallback.keepMeLoggedIn : state.keepMeLoggedIn || true,
            license:
                Object.keys(refreshedSession.license || {}).length > 0 ? refreshedSession.license : sessionFallback.license || state.license
        });
    };

    const updateUser = (userData = {}) => {
        const nextUser = { ...(state.user || {}), ...userData };

        persistSession({ user: nextUser });
        dispatch({ type: UPDATE_USER, payload: { user: nextUser } });
    };

    const updateLicense = (newLicense = {}) => {
        persistSession({ license: newLicense || {} });
        dispatch({ type: UPDATE_LICENSE, payload: { license: newLicense || {} } });
    };

    const login = async (username, password, keepMeLoggedIn) => {
        const response = await gestopApiForExternalEndpoints.post('security/login', { username, password });
        const { token, refresh_token: refreshTokenValue, user, profileSettings, license } = response?.data || {};

        hydrateSession({
            serviceToken: token || null,
            refreshToken: refreshTokenValue || null,
            keepMeLoggedIn: Boolean(keepMeLoggedIn),
            user: user || {},
            profileSettings: profileSettings || [],
            license: license || {}
        });
    };

    const register = async (username, email, password, name, lastName) => {
        await gestopApiForExternalEndpoints.post('security/register', {
            username,
            email,
            password,
            name,
            lastName
        });
    };

    const logout = async (successCallback = () => {}, errorCallback = () => {}) => {
        await gestopAPI
            .get('security/logout')
            .then((response) => {
                dumpUser();

                if (typeof successCallback === 'function') {
                    successCallback(response);
                }
            })
            .catch((error) => {
                dumpUser();

                if (typeof errorCallback === 'function') {
                    errorCallback(errorProcessor(error?.response?.data));
                }
            });
    };

    const requestLoginLink = async (usernameOrEmail = '', successCallback = () => {}, errorCallback = () => {}) => {
        await gestopApiForExternalEndpoints
            .post('security/requestloginlink', { usernameOrEmail })
            .then((response) => {
                if (typeof successCallback === 'function') {
                    successCallback(response);
                }
            })
            .catch((error) => {
                if (typeof errorCallback === 'function') {
                    errorCallback(errorProcessor(error?.response?.data));
                }
            });
    };

    const resetPassword = async (username, newPassword, successCallback = () => {}, errorCallback = () => {}) => {
        await gestopApiForExternalEndpoints
            .post('security/resetpassword', { username, newPassword, mode: newPassword ? 'withPassword' : 'withoutPassword' })
            .then((response) => {
                if (typeof successCallback === 'function') {
                    successCallback(response);
                }
            })
            .catch((error) => {
                if (typeof errorCallback === 'function') {
                    errorCallback(errorProcessor(error?.response?.data));
                }
            });
    };

    const updateProfileSetting = (settingName = '', settingData = {}, successCallback = () => {}, errorCallback = () => {}) => {
        const profileSettings = state.profileSettings || [];
        const profSettingIdx = profileSettings.findIndex((setting) => setting?.name === settingName);

        if (profSettingIdx === -1) {
            ApiProfileSettingsPostCollection({
                data: { user: getIriFromObj(state.user), name: settingName, ...settingData },
                successCallback: (response) => {
                    const nextProfileSettings = [...profileSettings, response];

                    persistSession({ profileSettings: nextProfileSettings });
                    dispatch({ type: UPDATE_PROFILE_SETTINGS, payload: { profileSettings: nextProfileSettings } });

                    if (typeof successCallback === 'function') {
                        successCallback();
                    }
                },
                errorCallback: (error) => {
                    if (typeof errorCallback === 'function') {
                        errorCallback(error);
                    }
                }
            });
        } else if (!hasSameProfileSettingValue(profileSettings.at(profSettingIdx)?.fieldValue, settingData?.fieldValue)) {
            ApiProfileSettingsPatchItem({
                id: profileSettings.at(profSettingIdx)?.id,
                data: settingData,
                successCallback: (response) => {
                    const nextProfileSettings = [...profileSettings];
                    nextProfileSettings.splice(profSettingIdx, 1, response);

                    persistSession({ profileSettings: nextProfileSettings });
                    dispatch({ type: UPDATE_PROFILE_SETTINGS, payload: { profileSettings: nextProfileSettings } });

                    if (typeof successCallback === 'function') {
                        successCallback();
                    }
                },
                errorCallback: (error) => {
                    if (typeof errorCallback === 'function') {
                        errorCallback(error);
                    }
                }
            });
        } else if (typeof successCallback === 'function') {
            successCallback();
        }
    };

    const deleteProfileSettings = (settingsIds = [], successCallback = () => {}, errorCallback = () => {}) => {
        if (settingsIds?.length === 0) return;

        ApiProfileSettingsDeleteCollection({
            ids: settingsIds,
            successCallback: () => {
                const nextProfileSettings = (state.profileSettings || []).filter(
                    (profileSetting) => !settingsIds.includes(profileSetting?.id)
                );

                persistSession({ profileSettings: nextProfileSettings });
                dispatch({ type: DELETE_PROFILE_SETTINGS, payload: { settingsIds } });

                if (typeof successCallback === 'function') {
                    successCallback();
                }
            },
            errorCallback: (error) => {
                if (typeof errorCallback === 'function') {
                    errorCallback(error);
                }
            }
        });
    };

    useEffect(() => {
        let isMounted = true;

        const init = async () => {
            try {
                const storedSession = loadAuthSession();

                if (!storedSession) {
                    if (isMounted) {
                        dispatch({ type: LOGOUT });
                    }
                    return;
                }

                const hasValidToken = verifyToken(storedSession.serviceToken);

                if (hasValidToken && hasUserData(storedSession.user)) {
                    if (isMounted) {
                        hydrateSession(storedSession);
                    }
                    return;
                }

                if (storedSession.keepMeLoggedIn && storedSession.refreshToken) {
                    const refreshedSession = await refreshToken(storedSession.refreshToken, storedSession);

                    if (!isMounted || refreshedSession) {
                        return;
                    }
                }

                if (isMounted) {
                    dumpUser();
                }
            } catch (error) {
                if (isMounted) {
                    dumpUser();
                }
            }
        };

        init();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!state?.isInitialized) {
        return <Loader />;
    }

    const contextState = { ...state };
    delete contextState.serviceToken;
    delete contextState.refreshToken;

    return (
        <JWTContext.Provider
            value={{
                ...contextState,
                login,
                refreshToken,
                logout,
                register,
                requestLoginLink,
                resetPassword,
                updateUser,
                updateLicense,
                dumpUser,
                updateProfileSetting,
                deleteProfileSettings
            }}
        >
            {children}
        </JWTContext.Provider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
